import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import UserPage from 'flarum/forum/components/UserPage';
import LinkButton from 'flarum/common/components/LinkButton';

/**
 * fof/byobu patch (forum side).
 *
 * 1. Nav item on your own profile
 *
 * byobu's own User.js adds a "Private discussions" nav item to *another* user's
 * profile, but bails on your own profile:
 *
 *     if (app.session.user === this.user) return;
 *
 * We want the tab on our own profile too. byobu registers the
 * `byobuUserPrivate` route (`/u/:username/private`) for everyone and its page
 * component works for the profile owner, so only the nav item is missing.
 *
 * byobu adds its item with `extend`, so the original runs first; this runs
 * after it. We only add the link when it is our own profile and byobu did not
 * already add it (guard against byobu changing its condition later).
 *
 * 2. Horizontal nav strip loses the active tab
 *
 * On tablet/phone widths core turns the profile nav into a horizontal
 * scrolling strip (`.sideNav--horizontal`: white-space: nowrap; overflow: auto).
 * Every route change re-renders the <ul>, which resets its scrollLeft to 0, so
 * a tab further along the strip (like this one) scrolls out of view even though
 * it is the active tab. Core never brings it back into view.
 *
 * We scroll the active tab into view, but only when the active tab actually
 * changed — never on a plain redraw — so a strip the user scrolled by hand is
 * left alone.
 *
 * 3. blomstra/search hijacks the "Private discussions" tab and empties it
 *
 * byobu 1.4.6's private tab loads its list with `filter[q]=byobu:<slug> is:private`.
 * When blomstra/search is installed it routes every discussion list that carries
 * a `filter.q` to its own endpoint (`/api/blomstra/search/discussions`). That
 * search backend does not understand byobu's `byobu:` / `is:private` gambits, so
 * it returns an empty set and the tab shows nothing.
 *
 * Flarum core resolves the exact same query correctly
 * (`GET /api/discussions?filter[q]=byobu:<slug> is:private` returns the rows), so
 * the fix is: while the byobu private route is active, rewrite the outgoing
 * request URL from blomstra's search endpoint back to core's `/api/discussions`.
 * Every other page keeps blomstra's behaviour untouched.
 *
 * We wrap `app.request`. blomstra builds the URL before the request goes out, so
 * by the time the options object reaches `app.request` the path already points
 * at `/api/blomstra/search/discussions`. We only rewrite when the byobu private
 * route is the active one (checked from the pathname, which is stable across
 * versions) and the URL is exactly that search endpoint.
 */
app.initializers.add('happydigital-byobu-hide-profile', () => {
  extend(UserPage.prototype, 'navItems', function (items) {
    if (!app.session.user || app.session.user !== this.user) return;

    // byobu already added it for some reason — don't duplicate.
    if (items.has('byobu')) return;

    const href = app.route('byobuUserPrivate', { username: this.user.slug() });

    items.add(
      'byobu',
      LinkButton.component(
        {
          href,
          icon: app.forum.attribute('byobu.icon-badge') || 'far fa-map',
        },
        app.translator.trans('fof-byobu.forum.user.byobu_link')
      ),
      85
    );
  });

  const revealActiveNavItem = function () {
    // Only the sidebar nav on the user profile, and only while it is the
    // horizontal scrolling strip (the desktop layout is a plain vertical list
    // with no overflow, so the width check below turns this into a no-op).
    const nav = this.element && this.element.querySelector('.UserPage-nav');
    if (!nav) return;

    const strip = nav.querySelector('ul') || nav;
    const active = strip.querySelector('li > .active, li > a.active');
    if (!active) return;

    const activeHref = active.getAttribute('href') || '';

    // Run only when the active tab changed since the last time we looked.
    // A redraw that keeps the same active tab (or a hand scroll) is ignored.
    if (this.__byobuLastActiveNav === activeHref) return;
    this.__byobuLastActiveNav = activeHref;

    if (strip.scrollWidth <= strip.clientWidth) return;

    const item = active.closest('li') || active;
    const itemLeft = item.offsetLeft;
    const itemRight = itemLeft + item.offsetWidth;
    const viewLeft = strip.scrollLeft;
    const viewRight = viewLeft + strip.clientWidth;

    if (itemLeft >= viewLeft && itemRight <= viewRight) return;

    // Nudge just enough to reveal it, biased to the left edge so the active
    // tab reads as "current" rather than being pinned to the right.
    strip.scrollLeft = Math.max(0, itemLeft - 16);
  };

  extend(UserPage.prototype, 'oncreate', revealActiveNavItem);
  extend(UserPage.prototype, 'onupdate', revealActiveNavItem);

  // --- 3. Keep blomstra/search off byobu's private tab ------------------------

  // byobu's private route is `/u/:username/private`. The pathname check is
  // version-stable; `app.current`'s route-name accessor is not.
  const onByobuPrivateRoute = function () {
    try {
      return /\/u\/[^/]+\/private\/?$/.test(window.location.pathname);
    } catch (e) {
      return false;
    }
  };

  const SEARCH_PATH = '/api/blomstra/search/discussions';
  const CORE_PATH = '/api/discussions';

  override(app, 'request', function (original, options) {
    if (
      options &&
      typeof options.url === 'string' &&
      onByobuPrivateRoute() &&
      options.url.indexOf(SEARCH_PATH) !== -1
    ) {
      // Rewrite blomstra's search endpoint back to core. `filter[q]` and the
      // rest of the query string are preserved; core understands the byobu
      // gambits that the search backend drops.
      const rewritten = Object.assign({}, options, {
        url: options.url.replace(SEARCH_PATH, CORE_PATH),
      });

      return original(rewritten);
    }

    return original(options);
  });
});
